import React from 'react'
import { motion } from 'framer-motion'
import { LogOut, Wallet } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useWeb3Wallet } from '../contexts/Web3WalletProvider'
import { ConnectButton } from '@rainbow-me/rainbowkit'

const Navigation: React.FC = () => {
  const location = useLocation()
  const { isConnected, disconnect, address: walletAddress, balance } = useWeb3Wallet()

  const navItems = [
    {
      path: '/',
      label: 'Home',
      exact: true
    },
    {
      path: '/presale',
      label: 'Presale'
    },
    {
      path: '/airdrop',
      label: 'Airdrop'
    },
    {
      path: '/history',
      label: 'History'
    },
    {
      path: '/settings',
      label: 'Settings'
    }
  ]

  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const formatAddress = (address: string | null) => {
    if (!address) return 'Not connected'
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const formatBalance = (balance: string) => {
    const num = parseFloat(balance)
    if (num < 0.001) {
      return balance
    }
    return num.toFixed(4)
  }

  const handleDisconnect = () => {
    if (window.confirm('Are you sure you want to disconnect your wallet?')) {
      disconnect()
    }
  }

  return (
    <>
      {/* Desktop Navigation - Top Bar */}
      <motion.nav
        className="hidden md:block fixed top-0 left-0 right-0 z-40 p-4"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="glass rounded-2xl border border-white/20 px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center">
                  <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
                </div>
                <span className="text-white font-bold text-lg">
                  Voice Pay
                </span>
              </div>

              {/* Navigation Links */}
              <div className="flex items-center space-x-1">
                {navItems.map((item) => {
                  const active = isActive(item.path, item.exact)
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                        active
                          ? 'bg-white/20 text-white'
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                      {active && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg"
                          layoutId="activeTab"
                          transition={{ type: 'spring', duration: 0.5 }}
                        />
                      )}
                    </Link>
                  )
                })}
              </div>

              {/* Wallet Info */}
              <div className="flex items-center space-x-3">
                {isConnected ? (
                  <>
                    <div className="text-right">
                      <div className="text-white text-sm font-medium">
                        {formatBalance(balance)} BNB
                      </div>
                      <div className="text-white/70 text-xs">
                        {formatAddress(walletAddress)}
                      </div>
                    </div>
                    <button
                      onClick={handleDisconnect}
                      className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200"
                      title="Disconnect Wallet"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <ConnectButton.Custom>
                    {({ openConnectModal }) => (
                      <button
                        onClick={openConnectModal}
                        className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
                      >
                        <Wallet className="w-4 h-4" />
                        <span>Connect Wallet</span>
                      </button>
                    )}
                  </ConnectButton.Custom>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Navigation - Bottom Bar */}
      <motion.nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 p-4"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="glass rounded-2xl border border-white/20 px-4 py-3">
          <div className="flex items-center justify-around">
            {navItems.map((item) => {
              const active = isActive(item.path, item.exact)
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative p-3 rounded-xl transition-all duration-200 flex flex-col items-center space-y-1 ${
                    active
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
                  <span className="text-xs font-medium">{item.label}</span>
                  {active && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl"
                      layoutId="activeMobileTab"
                      transition={{ type: 'spring', duration: 0.5 }}
                    />
                  )}
                </Link>
              )
            })}
            
            {/* Wallet Info Button */}
            {isConnected ? (
              <button
                onClick={handleDisconnect}
                className="relative p-3 rounded-xl transition-all duration-200 flex flex-col items-center space-y-1 text-white/70 hover:text-white hover:bg-white/10"
              >
                <div className="w-5 h-5 rounded-full bg-yellow-500 flex items-center justify-center">
                  <img src="/Voice-32x32.svg" alt="Voice" className="w-3 h-3" />
                </div>
                <span className="text-xs font-medium">Wallet</span>
              </button>
            ) : (
              <ConnectButton.Custom>
                {({ openConnectModal }) => (
                  <button
                    onClick={openConnectModal}
                    className="relative p-3 rounded-xl transition-all duration-200 flex flex-col items-center space-y-1 text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <Wallet className="w-5 h-5" />
                    <span className="text-xs font-medium">Connect</span>
                  </button>
                )}
              </ConnectButton.Custom>
            )}
          </div>
        </div>
      </motion.nav>

      {/* Mobile Wallet Info - Top Bar */}
      <motion.div
        className="md:hidden fixed top-0 left-0 right-0 z-40 p-4"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="glass rounded-xl border border-white/20 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded-lg bg-yellow-500 flex items-center justify-center">
                <img src="/Voice-32x32.svg" alt="Voice" className="w-3 h-3" />
              </div>
              <span className="text-white font-medium text-sm">
                Voice Pay
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {isConnected ? (
                <div className="text-right">
                  <div className="text-white text-sm font-medium">
                    {formatBalance(balance)} BNB
                  </div>
                  <div className="text-white/70 text-xs">
                    {formatAddress(walletAddress)}
                  </div>
                </div>
              ) : (
                <ConnectButton.Custom>
                  {({ openConnectModal }) => (
                    <button
                      onClick={openConnectModal}
                      className="flex items-center space-x-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-1 px-3 rounded-lg transition-all duration-200 text-sm"
                    >
                      <Wallet className="w-3 h-3" />
                      <span>Connect</span>
                    </button>
                  )}
                </ConnectButton.Custom>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Spacer for fixed navigation */}
      <div className="h-20 md:h-24" />
    </>
  )
}

export default Navigation